import { useCallback } from "react";
import { ArrowLeft, ArrowRight, Columns, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TARGET_COLUMNS } from "@/server/import/types";
import type { ColumnMapping } from "@/server/import/types";
import type { WizardState, WizardAction } from "../ImportPage";

interface Props {
  state: WizardState;
  dispatch: React.Dispatch<WizardAction>;
}

const TARGET_LABELS: Record<string, string> = {
  name: "Name",
  lat: "Latitude",
  lng: "Longitude",
  country: "Country",
  city: "City",
  hub: "Hub",
  location_type: "Location Type",
  sport_court_type: "Sport Court Type",
  tiering: "Tiering",
  typology: "Typology",
  visibility: "Visibility",
  accessibility: "Accessibility",
  number_of_courts: "Number of Courts",
  address: "Address",
  zip_code: "Zip Code",
  municipality: "Municipality",
  ownership: "Ownership",
  comments: "Comments",
  notes: "Notes",
  COMBINED_COORDS: "Combined Coordinates (lat, lng)",
};

function confidenceBadge(confidence: number) {
  if (confidence >= 0.95) {
    return <Badge variant="default" className="bg-green-600 text-[10px]">High</Badge>;
  }
  if (confidence >= 0.5) {
    return <Badge variant="secondary" className="bg-yellow-500/20 text-yellow-700 dark:text-yellow-400 text-[10px]">Medium</Badge>;
  }
  return <Badge variant="outline" className="text-muted-foreground text-[10px]">None</Badge>;
}

export function MappingStep({ state, dispatch }: Props) {
  const { columnMappings, parseResult } = state;

  const handleMappingChange = useCallback(
    (sourceHeader: string, targetColumn: string) => {
      const newMappings = columnMappings.map((m) => {
        if (m.sourceHeader === sourceHeader) {
          return {
            ...m,
            targetColumn: targetColumn === "_skip" ? null : targetColumn,
            confidence: targetColumn === "_skip" ? 0 : 1.0,
          };
        }
        return m;
      });
      dispatch({ type: "UPDATE_MAPPINGS", mappings: newMappings });
    },
    [columnMappings, dispatch],
  );

  // Check which targets are already used
  const usedTargets = new Set(
    columnMappings
      .map((m) => m.targetColumn)
      .filter((t): t is string => t !== null),
  );

  const hasNameMapping = columnMappings.some(
    (m) => m.targetColumn === "name",
  );
  const hasCoordMapping =
    columnMappings.some((m) => m.targetColumn === "COMBINED_COORDS") ||
    (columnMappings.some((m) => m.targetColumn === "lat") &&
      columnMappings.some((m) => m.targetColumn === "lng"));

  const canProceed = hasNameMapping;

  return (
    <div className="space-y-6">
      {/* Coordinate format indicator */}
      <Card>
        <CardContent className="p-4 flex items-center gap-3">
          <MapPin className="w-5 h-5 text-primary" />
          <div>
            <p className="text-sm font-medium">
              Coordinate format:{" "}
              <span className="text-primary">
                {parseResult?.coordinateFormat === "combined"
                  ? "Combined (lat, lng in one column)"
                  : "Separate (lat and lng columns)"}
              </span>
            </p>
            {parseResult?.coordinateSourceColumns &&
              parseResult.coordinateSourceColumns.length > 0 && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  Source: {parseResult.coordinateSourceColumns.join(", ")}
                </p>
              )}
          </div>
        </CardContent>
      </Card>

      {/* Column mapping table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Columns className="w-4 h-4" />
            Column Mapping
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50 border-b">
                  <th className="text-left p-3 font-medium">
                    Source Column
                  </th>
                  <th className="text-left p-3 font-medium">
                    Maps To
                  </th>
                  <th className="text-center p-3 font-medium w-24">
                    Confidence
                  </th>
                </tr>
              </thead>
              <tbody>
                {columnMappings.map((mapping) => (
                  <tr
                    key={mapping.sourceHeader}
                    className="border-b last:border-b-0"
                  >
                    <td className="p-3 font-mono text-xs">
                      {mapping.sourceHeader}
                    </td>
                    <td className="p-3">
                      <Select
                        value={mapping.targetColumn ?? "_skip"}
                        onValueChange={(val) =>
                          handleMappingChange(mapping.sourceHeader, val)
                        }
                      >
                        <SelectTrigger className="w-[240px] h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_skip">
                            <span className="text-muted-foreground">
                              (skip)
                            </span>
                          </SelectItem>
                          <SelectItem value="COMBINED_COORDS">
                            Combined Coordinates
                          </SelectItem>
                          {TARGET_COLUMNS.map((col) => (
                            <SelectItem
                              key={col}
                              value={col}
                              disabled={
                                usedTargets.has(col) &&
                                mapping.targetColumn !== col
                              }
                            >
                              {TARGET_LABELS[col] ?? col}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-3 text-center">
                      {confidenceBadge(mapping.confidence)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Sample data preview */}
          {parseResult && parseResult.sampleRows.length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-medium mb-2 text-muted-foreground">
                Sample Data (first {parseResult.sampleRows.length} rows)
              </h4>
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-muted/50 border-b">
                      {parseResult.headers.map((h) => (
                        <th key={h} className="text-left p-2 font-medium whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {parseResult.sampleRows.map((row, i) => (
                      <tr key={i} className="border-b last:border-b-0">
                        {parseResult.headers.map((h) => (
                          <td key={h} className="p-2 whitespace-nowrap max-w-[200px] truncate">
                            {row[h] ?? ""}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Validation warnings */}
      {!canProceed && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {!hasNameMapping && (
            <p>A "name" column must be mapped to proceed.</p>
          )}
        </div>
      )}

      {!hasCoordMapping && canProceed && (
        <div className="rounded-lg border border-yellow-500/50 bg-yellow-500/10 p-4 text-sm text-yellow-700 dark:text-yellow-400">
          No coordinate columns mapped. You'll configure address fields for
          geocoding in the next step.
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => dispatch({ type: "RESET" })}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button
          disabled={!canProceed}
          onClick={() =>
            dispatch({
              type: "GOTO_STEP",
              step: state.needsGeocoding ? "address" : "preview",
            })
          }
        >
          Next
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
